import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang, LANGUAGES } from "../i18n";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { PrimaryButton } from "../components/common";
import { Input } from "../components/ui/input";
import { Home, Check, KeyRound, ShieldCheck, Loader2, Globe, MailCheck, MessageCircle } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";

const SUPPORT_WA = (process.env.REACT_APP_SUPPORT_WHATSAPP || "").replace(/[^0-9]/g, "");

function WhatsAppSupport({ label }) {
  if (!SUPPORT_WA) return null;
  return (
    <a
      href={`https://wa.me/${SUPPORT_WA}?text=${encodeURIComponent("Hi My Manger team, I need help.")}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-support-link"
      className="flex items-center justify-center gap-2 w-full rounded-xl border border-[#25D366] bg-[#25D366]/10 text-[#128C4B] font-semibold text-sm py-3 transition active:scale-95 hover:bg-[#25D366]/20"
    >
      <MessageCircle className="w-4 h-4" /> {label}
    </a>
  );
}

export default function Onboarding() {
  const { t, lang, setLang } = useLang();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState(lang ? "auth" : "lang");
  const [mode, setMode] = useState("signup");
  const [role, setRole] = useState("tenant");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const pickLang = (code) => { setLang(code); setPhase("auth"); };

  const goForgot = () => { setErr(""); setResetSent(false); setMode("forgot"); };

  const doForgot = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: form.email });
      setResetSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const u = mode === "signup"
        ? await register({ ...form, role, language: lang || "en" })
        : await login(form.email, form.password);
      navigate(u?.role === "admin" ? "/admin" : "/home");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  if (phase === "lang") {
    return (
      <div className="min-h-screen bg-sand max-w-md mx-auto px-5 py-10 flex flex-col">
        <div className="flex items-center gap-2 mb-8 gs-rise">
          <div className="w-11 h-11 rounded-xl bg-terracotta flex items-center justify-center text-white"><Home className="w-6 h-6" strokeWidth={2.5} /></div>
          <div>
            <div className="font-head font-extrabold text-2xl text-slate-900">My Manger</div>
            <div className="text-sm text-slate-500">{t("tagline")}</div>
          </div>
        </div>
        <p className="font-head text-lg font-semibold text-slate-800 mb-6 leading-snug gs-rise" data-testid="value-prop">{t("value_prop")}</p>
        <h1 className="font-head text-2xl font-bold text-slate-900">{t("pick_language")}</h1>
        <p className="text-sm text-slate-500 mb-5">{t("pick_language_sub")}</p>
        <div className="grid grid-cols-2 gap-3">
          {LANGUAGES.map((l) => (
            <button key={l.code} onClick={() => pickLang(l.code)} data-testid={`language-select-${l.code}-btn`}
              className={`gs-card p-4 text-left transition active:scale-95 ${lang === l.code ? "ring-2 ring-terracotta" : ""}`}>
              <div className="font-head font-bold text-lg text-slate-900">{l.native}</div>
              <div className="text-xs text-slate-400 flex items-center gap-1">{l.en}{l.full && <Check className="w-3 h-3 text-trust" />}</div>
            </button>
          ))}
        </div>
        <div className="mt-8">
          <WhatsAppSupport label={t("wa_support")} />
        </div>
      </div>
    );
  }

  const submitLabel = mode === "signup" ? t("signup") : t("login");

  return (
    <div className="min-h-screen bg-sand max-w-md mx-auto px-5 py-10 flex flex-col justify-center">
      <div className="flex items-center gap-2 mb-6 gs-rise">
        <div className="w-11 h-11 rounded-xl bg-terracotta flex items-center justify-center text-white"><Home className="w-6 h-6" strokeWidth={2.5} /></div>
        <div className="font-head font-extrabold text-2xl text-slate-900 flex-1">{t("app_name")}</div>
        <DropdownMenu>
          <DropdownMenuTrigger data-testid="onboarding-language-switcher-btn" className="flex items-center gap-1 rounded-full bg-white border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm">
            <Globe className="w-4 h-4 text-terracotta" /> {LANGUAGES.find((l) => l.code === lang)?.native || "भाषा"}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
            {LANGUAGES.map((l) => (
              <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} data-testid={`onboarding-lang-opt-${l.code}`} className="cursor-pointer">
                <span className="font-medium">{l.native}</span><span className="text-slate-400 ml-2 text-xs">{l.en}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="gs-card p-5 gs-rise">
        {mode !== "forgot" && (
          <div className="flex rounded-xl bg-slate-100 p-1 mb-4 text-sm font-semibold">
            <button onClick={() => setMode("signup")} data-testid="tab-signup" className={`flex-1 py-2 rounded-lg ${mode === "signup" ? "bg-white shadow text-terracotta" : "text-slate-500"}`}>{t("signup")}</button>
            <button onClick={() => setMode("login")} data-testid="tab-login" className={`flex-1 py-2 rounded-lg ${mode === "login" ? "bg-white shadow text-terracotta" : "text-slate-500"}`}>{t("login")}</button>
          </div>
        )}

        {(() => {
          if (mode === "forgot") {
            if (resetSent) return (
            <div className="text-center py-4" data-testid="reset-sent-msg">
              <div className="mx-auto w-14 h-14 rounded-full bg-trust-light flex items-center justify-center mb-3"><MailCheck className="w-7 h-7 text-trust" /></div>
              <p className="font-head font-bold text-lg text-slate-900">{t("reset_email_sent")}</p>
              <p className="text-sm text-slate-500 mt-1 break-all">{form.email}</p>
              <button onClick={() => setMode("login")} data-testid="back-to-login-btn" className="text-sm font-semibold text-terracotta mt-5">{t("back_to_login")}</button>
            </div>
            );
            return (
            <>
              <h1 className="font-head text-xl font-bold text-slate-900 mb-1">{t("forgot_password")}</h1>
              <p className="text-sm text-slate-500 mb-4">{t("forgot_desc")}</p>
              <Input className="gs-tap mb-3 rounded-xl" placeholder={t("email")} data-testid="forgot-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              {err && <p className="text-sm text-red-500 mb-3" data-testid="auth-error">{err}</p>}
              <PrimaryButton onClick={doForgot} disabled={busy || !form.email} data-testid="send-reset-link-btn">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : t("send_reset_link")}
              </PrimaryButton>
              <button onClick={() => setMode("login")} data-testid="back-to-login-btn" className="w-full text-center text-sm font-semibold text-slate-500 mt-4">{t("back_to_login")}</button>
            </>
            );
          }
          return (
          <>
            {mode === "signup" && (
              <>
                <p className="text-xs font-semibold text-slate-500 mb-2">{t("i_am")}</p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <button onClick={() => setRole("tenant")} data-testid="signup-role-tenant" className={`gs-tap rounded-xl border-2 px-3 flex items-center justify-center gap-2 text-sm font-semibold ${role === "tenant" ? "border-terracotta bg-terracotta-light text-terracotta-dark" : "border-slate-200 text-slate-500"}`}><KeyRound className="w-4 h-4" />{t("tenant")}</button>
                  <button onClick={() => setRole("owner")} data-testid="signup-role-owner" className={`gs-tap rounded-xl border-2 px-3 flex items-center justify-center gap-2 text-sm font-semibold ${role === "owner" ? "border-terracotta bg-terracotta-light text-terracotta-dark" : "border-slate-200 text-slate-500"}`}><Home className="w-4 h-4" />{t("owner")}</button>
                </div>
                <Input className="gs-tap mb-3 rounded-xl" placeholder={t("full_name")} data-testid="signup-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <Input className="gs-tap mb-3 rounded-xl" placeholder={t("phone")} data-testid="signup-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </>
            )}
            <Input className="gs-tap mb-3 rounded-xl" placeholder={t("email")} data-testid="auth-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input type="password" className="gs-tap mb-2 rounded-xl" placeholder={t("password")} data-testid="auth-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            {mode === "login" && (
              <button onClick={goForgot} data-testid="forgot-password-btn" className="block ml-auto mb-3 text-sm font-semibold text-terracotta">{t("forgot_password")}</button>
            )}
            {err && <p className="text-sm text-red-500 mb-3" data-testid="auth-error">{err}{mode === "login" && (<> · <button onClick={goForgot} data-testid="reset-from-error-btn" className="underline font-semibold">{t("reset_password")}</button></>)}</p>}
            <PrimaryButton onClick={submit} disabled={busy} data-testid="auth-submit-btn">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : submitLabel}
            </PrimaryButton>
          </>
          );
        })()}

        <div className="flex items-center gap-1.5 justify-center mt-4 text-xs text-slate-400">
          <ShieldCheck className="w-3.5 h-3.5 text-trust" /> {t("encrypted_note")}
        </div>
      </div>

      <div className="mt-4">
        <WhatsAppSupport label={t("wa_support")} />
      </div>
    </div>
  );
}
